import { v4 as uuidv4 } from 'uuid'

import * as SentryVue from '@sentry/vue'
import { AppLauncher } from '@capacitor/app-launcher'
import { get, isNil } from 'lodash'

import { Dialog } from '@capacitor/dialog'

import { modalController } from '@ionic/vue'
import { Browser } from '@capacitor/browser'
import linkType from '@/constants/linkType'
import modalSize from '@/constants/modalSize'
import api from '@/services/api'
import { analytics } from '@/services/analytics'
import { useAuthHelpers } from '@/composables/authHelpers'
import { useLoadingOverlays } from '@/composables/loadingOverlays'
import { events } from '@/composables/events'
import Modal from '@/components/shared/Modal.vue'

const { openUrl } = useHelpers()

export function useModuleHelpers() {
    const appStore = useAppStore()
    const badgeStore = useBadgeStore()
    const deviceStore = useDeviceStore()
    const moduleStore = useModuleStore()

    const authHelpers = useAuthHelpers()
    const { snackbar } = useSnackbars()
    const loadingOverlays = useLoadingOverlays()

    function navigateBackToFirstModule(router, openModuleCount = null) {
        // TODO: Look into using vue memory router so that we have more control and can easily clear the stack etc.
        const stackLength = openModuleCount ?? moduleStore.stack.length

        const moduleId = first(moduleStore.stack)

        if (stackLength === 1) {
            // Replace the module, calling router.go(0) will result in a whole page reload
            router.replace({ name: 'view', params: { id: moduleId } })
        } else if (stackLength > 1) {
            // Navigate back
            const delta = (stackLength - 1) * -1
            router.go(delta)
        }

        moduleStore.loadModule(moduleId)
    }

    function navigateBackToRoot(router, openModuleCount) {
        // TODO: Look into using vue memory router so that we have more control and can easily clear the stack etc.
        const stackLength = openModuleCount ?? moduleStore.stack.length

        // Avoid calling router.go(0) which results in a page reload
        if (!stackLength) {
            return
        }

        router.go(stackLength * -1)
        moduleStore.reset()
    }

    function refreshInterface() {
        events.emit('refresh-interface')
    }

    function handleActions(router, actions, contextualEvents = null) {
        return reduce(actions, (promise, action) => {
            return promise.then((result) => {
                // Handle the action
                // NOTE: can return promises here
                switch (action.action_type) {
                    case 'snackbar':
                        return snackbar({
                            message: action.message,
                            type: action.type,
                            duration: action.duration,
                        })
                    case 'back':
                        // If there's a modal close it instead of going back in the router
                        return modalController.getTop()
                            // If there's an error getting the modal, assume there is none
                            .catch(() => null)
                            .then(async (modal) => {
                                let times = action.times || 1
                                if (modal) {
                                    await modalController.dismiss(null, 'cancel')
                                    times--
                                }
                                if (times > 0) {
                                    return router.go(times * -1)
                                }
                            })
                    case 'browser':
                        return new Promise((resolve) => {
                            const finished = async () => {
                                await Browser.removeAllListeners()
                                resolve()
                            }
                            Browser.addListener('browserFinished', finished)
                            if (action.close_on_load) {
                                Browser.addListener('browserPageLoaded', () => {
                                    setTimeout(() => {
                                        Browser.close()
                                            .catch((e) => console.log(e))
                                        finished()
                                    }, action.close_delay_ms || 0)
                                })
                            }
                            console.log('Opening browser from action', action.url)
                            Browser.open({ url: action.url })
                                .catch((e) => {
                                    console.log('Unable to open browser from action', action.url, e)
                                    SentryVue.captureException(e)
                                })
                        })
                    case 'close-module':
                        navigateBackToRoot(router)
                        return events.emit('close-module')
                    case 'mock-data-enable':
                        return appStore.enableMockData()
                    case 'mock-data-disable':
                        return appStore.disableMockData()
                    case 'refresh-badges':
                        return badgeStore.refreshAll()
                    case 'open-link':
                         
                        return openModuleOrOpenBrowserForLinkData(router, action.link, false, contextualEvents)
                    case 'pause':
                        return new Promise((resolve) => {
                            setTimeout(resolve, action.duration || 0)
                        })
                    case 'refresh':
                        if (moduleStore.currentModule?.id) {
                            return moduleStore.loadModule(moduleStore.currentModule.id)
                        }
                        return
                    case 'refresh-interface':
                        return refreshInterface()
                    case 'refresh-parent':
                        return moduleStore.refreshParent()
                    case 'replace-component':
                        if (moduleStore.currentModule?.id) {
                            return moduleStore.replaceData(moduleStore.currentModule.id, action.component)
                        }
                        return
                    case 'contextual-event':
                        if (contextualEvents) {
                            console.log('Emitting contextual event', { action, context: contextualEvents })
                            contextualEvents.emit(action.event, action)
                        } else {
                            console.log('Missing context for contextual event', { action })
                        }
                        return Promise.resolve()
                    case 'reset':
                        return appStore.resetStorage()
                    case 'restart':
                        return appStore.restart()
                    case 'set-user-context':
                        return authHelpers.handleUserContextChange(action.user_context)
                    default:
                        console.log('Unsupported action', action)
                        break
                }
            })
        }, Promise.resolve())
    }

    function openStore(link) {
        // Open the app store
        let storeUrl = link.url
        if (deviceStore.isIos) {
            storeUrl = `itms-apps://itunes.apple.com/app/${link.ios_app_id}`
        }
        if (deviceStore.isAndroid) {
            storeUrl = `market://details?id=${link.android_package}`
        }

        return AppLauncher.openUrl({
            url: storeUrl,
        })
    }

    async function getModuleOrOpenBrowserForLinkData(router, link, contextualEvents = null) {
        if (!router.go) {
            console.error('Vue router should be passed, not the ionic router')
        }

        if (!link || !link.type) {
            return
        }

        // link is external, just open url
        if (link.type === linkType.external) {
            openUrl(link)
            return
        }

        if (link.type === linkType.modal) {
            const modalConfig = link.component?.size === modalSize.fullscreen
                ? {}
                : {
                    cssClass: `ds-modal-bottom-sheet ds-modal-bottom-sheet-${link.component.size}`,
                    initialBreakpoint: 1,
                    breakpoints: [0, 1],
                }

            const modal = await modalController.create({
                component: Modal,
                componentProps: {
                    modal: link.component,
                },
                ...modalConfig,
            })
            await modal.present()
            return
        }

        if (link.type === linkType.externalApp) {
            // TODO should we display something more friendly for app links?
            analytics.setScreenName({ screenName: link.url, screenClass: 'External App' })

            // Handle opening external app on android
            if (deviceStore.isAndroid) {
                // NOTE: canOpenUrl ONLY works with package name on android
                const hasPackageInstalled = await AppLauncher.canOpenUrl({ url: link.android_package })

                // Open the store if the app isn't installed
                if (!hasPackageInstalled.value) {
                    return openStore(link)
                }

                // Try opening the app via schema
                const resultViaScheme = await AppLauncher.openUrl({ url: link.url })
                if (resultViaScheme.completed) {
                    return resultViaScheme
                }

                // Try opening the app via package name
                return AppLauncher.openUrl({ url: link.android_package })
            }

            // Handle opening external app on ios / web
            const canOpenUrl = await AppLauncher.canOpenUrl({ url: link.url })
            if (canOpenUrl.value) {
                return AppLauncher.openUrl({ url: link.url })
            }

            return openStore(link)
        }

        if (link.type === linkType.action) {
            return handleActions(router, link.actions, contextualEvents)
        }

        if (link.type === linkType.apiAction) {
            return api.post(link.url, link.payload || [])
                .then(({ data }) => handleActions(router, data.actions, contextualEvents))
                .catch((e) => {
                    if (authHelpers.isAuthenticationException(e)) {
                        // Forcible clear all loading overlays
                        loadingOverlays.clearAll()
                        return new Promise((resolve, reject) => {
                            authHelpers.handleAuthenticationException(
                                e.response.data,
                                router,
                                async ({ userSwitched } = { userSwitched: false }) => {
                                    // Remove the login view
                                    router.back()

                                    // Try again
                                     
                                    resolve(await getModuleOrOpenBrowserForLinkData(router, link, contextualEvents))
                                },
                                () => {
                                    router.back()

                                    // User has aborted, no action needed
                                    reject()
                                },
                                true,
                            )
                        })
                    }
                    throw e
                })
        }

        const newModule = {
            id: uuidv4(),
        }

        if (link.type === linkType.component) {
            newModule.data = {
                ...link.component,
            }
        }

        if (link.type === linkType.apiComponent) {
            // set active module url
            newModule.link = {
                type: linkType.apiComponent,
                url: link.url,
            }
            newModule.params = link.params || {}
        }

        if (link.type === linkType.prompt) {
            const prompt = link.prompt
            return Dialog.confirm({
                title: prompt.title,
                message: prompt.message,
                okButtonTitle: prompt.confirm_text,
                cancelButtonTitle: prompt.cancel_text,
            })
                .then((confirm) => {
                    if (!confirm.value) {
                        // If not confirmed return a resolved promise
                        return Promise.resolve()
                    }
                     
                    return handleClick(router, {
                        link: {
                            ...prompt.confirm_link,
                            // Include the payload, eg from form component
                            payload: link.payload,
                        },
                    }, null, true, contextualEvents)
                })
        }

        moduleStore.addModule(newModule)

        return Promise.resolve(newModule)
    }

    async function openModuleOrOpenBrowserForLinkData(router, link, replace = false, contextualEvents = null) {
        const appModule = await getModuleOrOpenBrowserForLinkData(router, link, contextualEvents)
        if (!appModule || !appModule.id) {
            return
        }
        const method = replace ? router.replace : router.push
        method({ name: 'view', params: { id: appModule.id } })
    }

    function handleClick(router, appModule, itemId, showLoadingOverlay = true, contextualEvents = null) {
        // To support CardDetailData links we need to check read and read_url
        const read = get(appModule, 'data.read') || get(appModule, 'read')
        const readUrl = get(appModule, 'meta.read') || get(appModule, 'read_url')

        if (read !== true && readUrl && !isNil(itemId)) {
            moduleStore.markAsRead(readUrl, itemId)
                .then(() => {
                    appModule.data.read = true
                })
                .catch((error) => {
                    console.log('Unable to mark as read', error)
                })
        }

        if (showLoadingOverlay && appModule.link?.type === 'api-action') {
            loadingOverlays.push()
        }

        // Don't catch exception here otherwise we can't handle validation errors etc.
        return openModuleOrOpenBrowserForLinkData(router, appModule.link, false, contextualEvents)
            .finally(() => {
                if (showLoadingOverlay) {
                    loadingOverlays.pop()
                }
            })
    }

    function setAnalyticsScreenFromConfig(config) {
        const analyticsScreenName = get(config, 'meta.analytics_title')
        analytics.setScreenName({
            screenName: analyticsScreenName,
        })
        SentryVue.addBreadcrumb({
            category: 'navigation',
            level: 'info',
            data: {
                screenName: analyticsScreenName,
            },
        })
    }

    function isActionException(e) {
        return get(e, 'response.status') === 400
            && get(e, 'response.data.type') === 'App\\Exceptions\\ExceptionWithActions'
            && get(e, 'response.data.actions')
    }

    function handleActionException(router, data) {
        handleActions(router, data.actions)
    }

    return {
        getModuleOrOpenBrowserForLinkData,
        openModuleOrOpenBrowserForLinkData,
        handleClick,
        handleActions,
        navigateBackToFirstModule,
        navigateBackToRoot,
        setAnalyticsScreenFromConfig,
        isActionException,
        handleActionException,
        refreshInterface,
    }
}
